import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { keys } from "../queryKeys.js";
import * as api from "../api/channels.js";

export function useChannels(options = {}) {
  return useQuery({
    queryKey: keys.channels.list,
    queryFn: api.listChannels,
    ...options,
  });
}

export function useChannel(id, options = {}) {
  return useQuery({
    queryKey: keys.channels.detail(id),
    queryFn: () => api.getChannel(id),
    enabled: !!id,
    ...options,
  });
}

export function useCreateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createChannel,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels.all }),
  });
}

export function useUpdateChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => api.updateChannel(id, data),
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: keys.channels.all });
      qc.invalidateQueries({ queryKey: keys.channels.detail(id) });
    },
  });
}

export function useDeleteChannel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.deleteChannel,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.channels.all }),
  });
}

export function useAddChannelMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, userId }) => api.addChannelMember(channelId, userId),
    onSuccess: (_d, { channelId }) =>
      qc.invalidateQueries({ queryKey: keys.channels.detail(channelId) }),
  });
}

export function useRemoveChannelMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, userId }) => api.removeChannelMember(channelId, userId),
    onSuccess: (_d, { channelId }) =>
      qc.invalidateQueries({ queryKey: keys.channels.detail(channelId) }),
  });
}

export function useAddChannelEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, data }) => api.addChannelEndpoint(channelId, data),
    onSuccess: (_d, { channelId }) =>
      qc.invalidateQueries({ queryKey: keys.channels.detail(channelId) }),
  });
}

export function useRemoveChannelEndpoint() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, endpointId }) =>
      api.removeChannelEndpoint(channelId, endpointId),
    onSuccess: (_d, { channelId }) =>
      qc.invalidateQueries({ queryKey: keys.channels.detail(channelId) }),
  });
}

export function useMonitorChannels(monitorId, options = {}) {
  return useQuery({
    queryKey: ["monitor", monitorId, "channels"],
    queryFn: () => api.getMonitorChannels(monitorId),
    enabled: !!monitorId,
    ...options,
  });
}

export function useSetMonitorChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ monitorId, channelIds }) =>
      api.setMonitorChannels(monitorId, channelIds),
    onSuccess: (_d, { monitorId }) => {
      qc.invalidateQueries({ queryKey: ["monitor", monitorId, "channels"] });
      qc.invalidateQueries({ queryKey: keys.monitors.detail(monitorId) });
    },
  });
}

export function useMyNotificationPreferences(options = {}) {
  return useQuery({
    queryKey: ["my-notification-preferences"],
    queryFn: api.getMyNotificationPreferences,
    ...options,
  });
}

export function useUpdateMyNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.updateMyNotificationPreferences,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["my-notification-preferences"] }),
  });
}
